import React from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { ArrowRight, ChevronRight, Briefcase, Stethoscope, ShieldCheck, BookOpen } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import JsonLd from "../components/JsonLd";
import { usePageSEO } from "../hooks/usePageSEO";
import { BUSINESS_PAGES, buildBusinessJsonLd } from "../data/businessPages";

const SECTION_ICONS = [BookOpen, ShieldCheck, Briefcase, Stethoscope];
const getSectionIcon = (i) => SECTION_ICONS[i] || BookOpen;

const BusinessPage = () => {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, "").split("/")[0];
  const page = BUSINESS_PAGES[slug];

  usePageSEO(page?.title, page?.description);

  if (!page) return <Navigate to="/" replace />;

  const jsonLd = buildBusinessJsonLd(page);
  const AudienceIcon = page.audience === "provider" ? Stethoscope : Briefcase;

  return (
    <div className="min-h-screen bg-[#fffaf5]" data-testid={`business-${page.slug}`}>
      <Header />
      <JsonLd id={`business-jsonld-${page.slug}`} data={jsonLd} />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-[#fdf2f4] to-[#fffaf5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center text-sm text-[#a09186] mb-6" data-testid="business-breadcrumb">
            <Link to="/" className="hover:text-[#b89a6d]">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-[#3a2a26] font-medium">{page.breadcrumb}</span>
          </nav>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fdf2f4] border border-[#e8c5b8] rounded-full mb-4">
            <AudienceIcon className="w-4 h-4 text-[#b89a6d]" />
            <span className="text-sm text-[#b89a6d] font-medium">{page.eyebrow}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3a2a26] mb-6" data-testid="business-h1">
            {page.h1}
          </h1>
          <p className="business-intro text-base md:text-lg text-[#5a4a40] max-w-3xl leading-relaxed">
            {page.intro}
          </p>
        </div>
      </section>

      {/* Sections */}
      <section className="py-12 bg-[#fffaf5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6" data-testid="business-sections">
          {page.sections.map((s, i) => {
            const SectionIcon = getSectionIcon(i);
            return (
              <article
                key={s.heading}
                className="bg-white border border-[#f0d8d4] rounded-2xl p-7"
                data-testid={`business-section-${i}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#fdf2f4] rounded-lg flex items-center justify-center">
                    <SectionIcon className="w-5 h-5 text-[#b89a6d]" />
                  </div>
                  <h2 className="text-base md:text-lg font-bold text-[#3a2a26]">{s.heading}</h2>
                </div>
                <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed">{s.body}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA block */}
      {page.cta && (
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-[#fdf2f4] to-[#fffaf5] border border-[#e8c5b8] rounded-2xl p-8" data-testid="business-cta">
              <h2 className="text-xl md:text-2xl font-bold text-[#3a2a26] mb-3">{page.cta.title}</h2>
              <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed mb-5">{page.cta.body}</p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to={page.cta.primaryTo}
                  data-testid="business-cta-primary"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#d4a5ad] hover:bg-[#c4949c] text-white font-semibold rounded-lg transition-colors text-sm"
                >
                  {page.cta.primaryLabel}
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to={page.cta.secondaryTo}
                  data-testid="business-cta-secondary"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-[#d4a5ad] text-[#b89a6d] hover:bg-[#fdf2f4] font-semibold rounded-lg transition-colors text-sm"
                >
                  {page.cta.secondaryLabel}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Internal links */}
      {page.internalLinks?.length > 0 && (
        <section className="py-12 bg-[#fffaf5]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-6">Keep exploring</h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="business-internal-links">
              {page.internalLinks.map((l) => {
                const linkClass = "px-4 py-3 bg-white border border-[#f0d8d4] hover:border-[#d4a5ad] rounded-lg text-[#5a4a40] hover:text-[#b89a6d] text-sm font-medium transition-colors flex items-center justify-between group";
                const testId = `business-link-${l.to.replace(/[/:.?#&=]/g, "-")}`;
                if (l.external) {
                  return (
                    <a
                      key={l.to}
                      href={l.to}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      data-testid={testId}
                      className={linkClass}
                    >
                      <span>{l.label}</span>
                      <ArrowRight className="w-4 h-4 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                    </a>
                  );
                }
                return (
                  <Link key={l.to} to={l.to} data-testid={testId} className={linkClass}>
                    <span>{l.label}</span>
                    <ArrowRight className="w-4 h-4 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {page.faqs?.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-8 text-center">
              {page.breadcrumb} — Frequently Asked Questions
            </h2>
            <div className="space-y-4" data-testid="business-faqs">
              {page.faqs.map((f, i) => (
                <details
                  key={f.q}
                  className="bg-[#fffaf5] border border-[#f0d8d4] rounded-xl p-5 group"
                  data-testid={`business-faq-${i}`}
                >
                  <summary className="cursor-pointer text-[#3a2a26] font-semibold list-none flex items-start justify-between gap-4">
                    <span>{f.q}</span>
                    <ChevronRight className="w-5 h-5 text-[#b89a6d] flex-shrink-0 group-open:rotate-90 transition-transform" />
                  </summary>
                  <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed mt-3">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Compliance banner */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#fdf2f4] border border-[#e8c5b8] rounded-xl p-6 text-center">
            <p className="text-sm text-[#5a4a40] leading-relaxed">
              <strong className="text-[#b89a6d]">Education, not dispensing.</strong> PeptidesForMedSpas is an educational catalog, not a pharmacy or medical practice. Research peptides are for laboratory use only. Clinical decisions and product administration remain with licensed clinicians and licensed pharmacy partners.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BusinessPage;
